import { useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Accordion } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import ActionButton from "../../formInputs/buttons/ActionButton";
import CommentEditor from "./CommentEditor";
import ElementaryProgressReportDao from "../../../dao/ElementaryProgressReportDao";
import Icon from "../../icon/Icon";
import isGradingProfileWindowOpen from "../../../utils/ActionControls/isGradingProfileWindowOpen";
import isEprEditable from "../../../utils/ActionControls/isEprEditable";
import LoadingSvg2 from "../../loadingSvg/LoadingSvg2";
import RadioSwitchButton from "../../formInputs/buttons/RadioSwitchButton";
import RatingProgressBar from "../RatingProgressBar";
import useStudentImages from "../../../hooks/useStudentImages";

import { gradeTranslation } from "../../../utils/SchoolAdmin/translations";
import { GlobalContext } from "../../contextProvider/ContextProvider";

import "../../../styles/GroupAccordionItem.scss";

/**
 * GroupAccordionItem component renders a single group within an accordion.
 * It displays group details, allows editing/deleting, and conditionally renders
 * the HomeroomGroupForm in a modal when editing.
 *
 * @component
 * @param {Object} dispatch
 * @param {string} eventKey
 * @param {Object} group
 * @param {Function} handleDeleteGroup
 * @param {Boolean} isActiveAndIsUnreleased
 * @param {Function} onEdit
 * @param {Object} selectedEpr
 * @param {string} basePath
 */
const GroupAccordionItem = ({
    dispatch,
    eventKey,
    grade,
    group,
    groupRatingCounts,
    handleDeleteGroup,
    isActiveAndIsUnreleased,
    onEdit,
    selectedEpr,
    basePath,
    type = "HOMEROOM"
}) => {
    const navigate = useNavigate();
    const { state: globalState } = useContext(GlobalContext);
    const { actionWindowsDto, schoolYearDto, token, userDetails } = globalState || {};

    const initialSubjectVisibility =
        grade?.subjectToStandards?.reduce((acc, subject) => {
            acc[subject.templateId] = false;
            return acc;
        }, {}) || {};

    const [ratingCounts, setRatingCounts] = useState(groupRatingCounts);
    const [savingSleComment, setSavingSleComment] = useState(false);
    const [savingTemplateId, setSavingTemplateId] = useState(null);
    const [sharedGroupProfileDto, setSharedGroupProfileDto] = useState(null);
    const [sleCommentDraft, setSleCommentDraft] = useState("");
    const [sleCommentVisible, setSleCommentVisible] = useState(false);
    const [subjectCommentsVisible, setSubjectCommentsVisible] = useState(initialSubjectVisibility);
    const [viewMode, setViewMode] = useState("SUBJECTS");

    // drafts keyed by templateId (so typing doesn’t mutate the dto until Save)
    const [subjectCommentDrafts, setSubjectCommentDrafts] = useState({});

    const normalizeKey = (group = {}) => {
        const { key, value } = group;
        if (key) return key;
        if (typeof value !== "string") return value;

        const dash = value.indexOf("-");
        return dash >= 0 ? value.slice(dash + 1) : value;
    };

    const normalizedKey = normalizeKey(group);

    const filteredStudents = useMemo(() => {
        if (type === "HOMEROOM") {
            return (group?.homeroomGroupStudents ?? []).filter((s) => s.firstName !== null);
        }
        return group?.sharedMeta?.sharedGroupStudents ?? [];
    }, [group, type]);

    const sortedStudents = useMemo(() => {
        return [...filteredStudents].sort((a, b) => (a.lastName ?? "").localeCompare(b.lastName ?? ""));
    }, [filteredStudents]);
    const toggleSubjectCommentVisibility = (templateId) => {
        setSubjectCommentsVisible((prev) => ({
            ...prev,
            [templateId]: !prev[templateId]
        }));
    };

    const seedSubjectCommentDraftIfMissing = (templateId, savedValue) => {
        setSubjectCommentDrafts((prev) => {
            if (Object.prototype.hasOwnProperty.call(prev, templateId)) return prev;
            return {
                ...prev,
                [templateId]: savedValue ?? ""
            };
        });
    };

    const saveGradingProfileComments = async (dto) => {
        if (!dto?.key || !token) return null;

        // eslint-disable-next-line no-unused-vars
        const { gradingProfileStudents, ...dataToSave } = dto;

        const options = {
            action: "updateGradingProfileComments",
            gradingProfileId: dto.key,
            data: { ...dataToSave, lastUpdaterGuid: userDetails.uid },
            token
        };

        try {
            const resp = await ElementaryProgressReportDao(options);
            return resp?.data?.payload ?? null;
        } catch (error) {
            console.error("Error saving grading profile comments:", error);
            toast.error("Comment was not saved. Please make note of your comment and try again later.");
        }
    };

    const handleSaveSubjectComment = async (subject) => {
        if (!sharedGroupProfileDto?.key || !subject?.templateId || !token) return;

        const templateId = subject.templateId;

        try {
            setSavingTemplateId(templateId);

            const nextDto = {
                ...sharedGroupProfileDto,
                gradingCriteriaList: sharedGroupProfileDto.gradingCriteriaList.map((c) =>
                    String(c.parentTemplate) === String(templateId)
                        ? { ...c, comment: subjectCommentDrafts[templateId] ?? c.comment ?? "" }
                        : c
                )
            };

            const updated = await saveGradingProfileComments(nextDto);
            if (updated) {
                setSharedGroupProfileDto(updated);

                // clear the draft so it falls back to saved value next render
                setSubjectCommentDrafts((prev) => {
                    const copy = { ...prev };
                    delete copy[templateId];
                    return copy;
                });
                toast.success("Comment successfully saved.");
            } else {
                toast.error("Comment was not saved. Please make note of your comment and try again later.");
            }
        } catch (e) {
            console.error("Error saving subject comment:", e);
            toast.error("Comment was not saved. Please make note of your comment and try again later.");
        } finally {
            setSavingTemplateId(null);
        }
    };

    const handleSaveSleComment = async () => {
        if (!sharedGroupProfileDto?.key || !token) return;

        try {
            setSavingSleComment(true);

            const nextDto = {
                ...sharedGroupProfileDto,
                sleComment: sleCommentDraft ?? sharedGroupProfileDto?.sleComment ?? ""
            };
            const updated = await saveGradingProfileComments(nextDto);
            if (updated) {
                setSharedGroupProfileDto(updated);
                setSleCommentDraft(updated?.sleComment ?? "");
                toast.success("Comment successfully saved.");
            } else {
                toast.error("Comment was not saved. Please make note of your comment and try again later.");
            }
        } catch (e) {
            console.error("Error saving SLE comment:", e);
            toast.error("Comment was not saved. Please make note of your comment and try again later.");
        } finally {
            setSavingSleComment(false);
        }
    };

    const handleShareGroupClick = () => {
        dispatch({ type: "SET_SELECTED_EPR", payload: selectedEpr });
        const path = basePath
            ? `${basePath}/summary/share/${selectedEpr.key}/groups/${group.key}`
            : `/home/summary/share/${selectedEpr.key}/groups/${group.key}`;
        navigate(path);
    };

    const handleViewModeChange = (e) => {
        setViewMode(e.target.value);
    };

    const getCriteriaForSubject = (subject) => {
        if (!sharedGroupProfileDto?.gradingCriteriaList || !subject?.templateId) return null;
        return sharedGroupProfileDto.gradingCriteriaList.find(
            (c) => String(c.parentTemplate) === String(subject.templateId)
        );
    };

    const getDraftOrSavedComment = (subject) => {
        const templateId = subject.templateId;
        if (Object.prototype.hasOwnProperty.call(subjectCommentDrafts, templateId)) {
            return subjectCommentDrafts[templateId];
        }
        const criteria = getCriteriaForSubject(subject);
        return criteria?.comment ?? "";
    };

    const handleInputRatingsClick = (subject) => {
        const path = `${basePath || "/home"}/summary/input-ratings/groups`;
        navigate(path, {
            state: {
                selectedGrade: gradeTranslation[grade.gradeLevel] ?? group.sharedGradeName,
                subjectId: subject.templateId,
                preselectedGroupIds: [String(normalizedKey)],
                selectedEpr: selectedEpr,
                selectedSchoolLocationKey: group.sharedLocationKey
            }
        });
    };

    const handleInputSleRatingsClick = () => {
        const path = `${basePath || "/home"}/summary/input-ratings/groups`;
        navigate(path, {
            state: {
                selectedGrade: gradeTranslation[grade.gradeLevel] ?? group.sharedGradeName,
                sleSelected: true,
                preselectedGroupIds: [String(normalizedKey)],
                selectedEpr: selectedEpr,
                gradeId: grade,
                selectedSchoolLocationKey: group.sharedLocationKey
            }
        });
    };

    const buildStudentLinkState = () => {
        const sharedGroupId = group?.sharedMeta?.sharedGroupId ?? group.key;
        return {
            mode: "GROUPS",
            sharedGroupId: sharedGroupId,
            preselectedGroupIds: [sharedGroupId]
        };
    };

    const handleOpenStudent = (student) => {
        const state = buildStudentLinkState();

        const viewPath = "/summary/input-ratings/student";

        const summaryPath = basePath
            ? `${basePath}${viewPath}/${student.studentNumber}`
            : `/home${viewPath}/${student.studentNumber}`;

        navigate(
            {
                pathname: summaryPath
            },
            { state }
        );
    };

    // Only load images when the user switches to STUDENTS view
    const shouldLoadImages = viewMode === "STUDENTS";

    const { images: studentImages, loading } = useStudentImages(
        shouldLoadImages ? filteredStudents : [],
        shouldLoadImages ? schoolYearDto?.key : null,
        shouldLoadImages ? token : null
    );

    /**
     * Get a group's permission for a subject, based on the subject's templateId
     * and the sharedGradingProfileDto's gradingCriteria mapping.
     *
     * @returns "EDIT" | "VIEW" | null
     */
    function getSubjectPermission(subject, group, sharedGradingProfileDto) {
        if (!subject?.templateId || !group?.sharedMeta?.groupShareDto?.gradingCriteriaShares) {
            return null;
        }

        // 1) Find the gradingCriteria that maps to this subject's templateId
        const criteria = sharedGradingProfileDto?.gradingCriteriaList?.find(
            (c) => String(c.parentTemplate) === String(subject.templateId)
        );
        if (!criteria) return null;

        // 2) Collect this group's shares for that gradingCriteriaId
        const shares = group.sharedMeta.groupShareDto.gradingCriteriaShares.filter(
            (s) => String(s.gradingCriteriaId) === String(criteria.key)
        );

        if (shares.length === 0) return null;

        // 3) Resolve strongest permission (EDIT > VIEW)
        const hasEdit = shares.some((s) => s.permission === "EDIT");
        if (hasEdit) return "EDIT";

        const hasView = shares.some((s) => s.permission === "VIEW");
        if (hasView) return "VIEW";

        return null;
    }

    useEffect(() => {
        if (!token || !group.sharedGradingProfile) return;
        if (!groupRatingCounts && !ratingCounts) {
            const options = {
                action: "getGroupRatingCounts",
                gradingProfileId: group.sharedGradingProfile,
                token
            };
            ElementaryProgressReportDao(options)
                .then((response) => {
                    if (response?.data?.payload) {
                        setRatingCounts(response.data.payload);
                    }
                })
                .catch((error) => {
                    toast.error("There was a problem loading rating counts.");
                    console.error("Error fetching group rating counts:", error);
                });
        }
    }, [group, groupRatingCounts, ratingCounts, token]);

    useEffect(() => {
        if (!token || !group.sharedGradingProfile) return;
        if (!sharedGroupProfileDto) {
            const options = {
                action: "getGradingProfileById",
                gradingProfileId: group.sharedGradingProfile,
                token
            };
            ElementaryProgressReportDao(options)
                .then((response) => {
                    const dto = response?.data?.payload;
                    if (dto) {
                        setSharedGroupProfileDto(dto);
                    }
                })
                .catch((error) => {
                    toast.error("There was a problem loading shared groups information. Please try again later.");
                    console.error("Error fetching grading profile:", error);
                });
        }
    }, [group, sharedGroupProfileDto, token]);

    const studentTableRows = sortedStudents.map((student) => {
        const imageUrl = studentImages[student.studentNumber];

        return (
            <tr key={student.key ?? student.studentNumber}>
                <td>
                    {imageUrl ? (
                        <img
                            src={imageUrl}
                            alt={`${student.firstName} ${student.lastName}`}
                            style={{ width: "45px", height: "60px", borderRadius: "5%" }}
                        />
                    ) : (
                        <Icon iconName="NO_STUDENT_IMAGE" fill="#19608F" height={60} width={45} />
                    )}
                    <span style={{ marginLeft: "8px" }}>
                        {student.lastName}, {student.firstName}
                    </span>
                </td>
                <td style={{ textAlign: "right", verticalAlign: "middle" }}>
                    <ActionButton
                        label={isActiveAndIsUnreleased ? "Input Ratings" : "View Ratings"}
                        onClick={() => handleOpenStudent(student)}
                    />
                </td>
            </tr>
        );
    });

    const isSharedWindowOpen = isGradingProfileWindowOpen(actionWindowsDto, sharedGroupProfileDto?.timePeriod);
    const isSharedActiveAndIsUnreleased = isEprEditable({
        isActiveWindow: isSharedWindowOpen,
        status: sharedGroupProfileDto?.status,
        userDetails
    });
    const isSleSharedGroup = group?.sharedMeta?.groupSleShareDto;
    const sleSharedPermission = group?.sharedMeta?.groupSleShareDto?.permission;
    const isSharedWholeClass =
        type === "SHARED" &&
        (sharedGroupProfileDto?.homeroomGroups?.some(
            (hg) => String(hg.key) === String(normalizedKey) && hg.type === "WHOLE_CLASS"
        ) ??
            false);
    const canEditSleComment =
        type === "SHARED" && isSharedWholeClass && sleSharedPermission === "EDIT" && isSharedActiveAndIsUnreleased;

    return (
        <Accordion.Item eventKey={eventKey} className="mb-3 group-accordion">
            <Accordion.Header>
                <span className="group-name-title">{group.name ?? group.label}</span>&ndash;{" "}
                <span className="group-student-count">{filteredStudents?.length} Students</span>
            </Accordion.Header>
            <Accordion.Body className="accordion-body-group">
                <div className="group-accordion-header-row">
                    <RadioSwitchButton
                        id={`view-mode-${normalizeKey(group)}`}
                        name="viewMode"
                        leftLabel="Subject Overview"
                        leftValue="SUBJECTS"
                        rightLabel="Student Overview"
                        rightValue="STUDENTS"
                        selectedValue={viewMode}
                        onChange={handleViewModeChange}
                    />
                    {type === "HOMEROOM" && (
                        <div className="group-accordion-btn-group">
                            <ActionButton label="Share Group" onClick={handleShareGroupClick} />
                            <ActionButton
                                className="action-button-reg"
                                disable={!isActiveAndIsUnreleased}
                                label="Edit Group"
                                onClick={onEdit}
                            />
                            <ActionButton
                                className="action-button-delete"
                                disable={!isActiveAndIsUnreleased}
                                label="Delete Group"
                                onClick={() => handleDeleteGroup(group.key)}
                            />
                        </div>
                    )}
                </div>
                <hr />
                {viewMode === "SUBJECTS" ? (
                    <>
                        <span className="group-name-title">Subjects</span>
                        <div className="epr-subjects-group">
                            {(type === "HOMEROOM" || isSleSharedGroup) && (
                                <div className="subject-card-group">
                                    <div className="subject-header-group">
                                        <span className="teacher-epr-subject-name-group">SLE</span>
                                        {/* Placeholder div to keep alignment the same with other subjects */}
                                        <div className="rating-count-container" />

                                        {type === "SHARED" && (
                                            <div className="permission-indicator">
                                                <div
                                                    className={`permission-indicator-text ${sleSharedPermission === "EDIT" ? "edit" : "view"}`}
                                                >
                                                    {sleSharedPermission === "VIEW" ? "View Only" : "Edit"}
                                                </div>
                                            </div>
                                        )}
                                        <div className="subject-header-right-group">
                                            <ActionButton
                                                label={
                                                    sleSharedPermission === "VIEW" ? "View Ratings" : "Input Ratings"
                                                }
                                                className="action-button-reg"
                                                onClick={() => handleInputSleRatingsClick()}
                                            />
                                        </div>
                                    </div>
                                    {type === "SHARED" && sharedGroupProfileDto && (
                                        <CommentEditor
                                            id="subject-comment-SLE"
                                            label="Add comment for SLE"
                                            placeholder="Add comment for SLE"
                                            isVisible={sleCommentVisible}
                                            onToggle={() => {
                                                setSleCommentVisible((prev) => !prev);
                                                setSleCommentDraft((prev) =>
                                                    prev !== "" ? prev : (sharedGroupProfileDto?.sleComment ?? "")
                                                );
                                            }}
                                            canEdit={canEditSleComment}
                                            isSaving={savingSleComment}
                                            value={sleCommentDraft}
                                            onChange={(next) => setSleCommentDraft(next)}
                                            onSave={handleSaveSleComment}
                                            viewValue={sharedGroupProfileDto?.sleComment ?? ""}
                                        />
                                    )}
                                </div>
                            )}

                            {grade &&
                                grade.subjectToStandards.map((subject) => {
                                    const subjectPerm =
                                        type === "HOMEROOM"
                                            ? "EDIT"
                                            : getSubjectPermission(subject, group, sharedGroupProfileDto);

                                    const matchGroup = ratingCounts?.find(
                                        (gRc) => parseInt(gRc.groupId, 10) === parseInt(normalizedKey, 10)
                                    );

                                    const countObject = matchGroup?.groupRatingCounts?.find(
                                        (obj) => obj.subject === subject.subjectName
                                    );

                                    if (!subjectPerm) return;

                                    return (
                                        <div key={subject.templateId} className="subject-card-group">
                                            <div className="subject-header-group">
                                                <span className="teacher-epr-subject-name-group">
                                                    {subject.subjectName}
                                                </span>
                                                {countObject && (
                                                    <div className="rating-count-container">
                                                        <div>
                                                            <RatingProgressBar countObject={countObject} />
                                                        </div>
                                                    </div>
                                                )}
                                                {type === "SHARED" && (
                                                    <div className="permission-indicator">
                                                        <div
                                                            className={`permission-indicator-text ${subjectPerm.toUpperCase() === "EDIT" ? "edit" : "view"}`}
                                                        >
                                                            {subjectPerm.toUpperCase() === "VIEW"
                                                                ? "View Only"
                                                                : "Edit"}
                                                        </div>
                                                    </div>
                                                )}
                                                <div className="subject-header-right-group">
                                                    <ActionButton
                                                        label={
                                                            subjectPerm.toUpperCase() === "VIEW"
                                                                ? " View Ratings"
                                                                : "Input Ratings"
                                                        }
                                                        className="action-button-reg"
                                                        onClick={() => handleInputRatingsClick(subject)}
                                                    />
                                                </div>
                                            </div>
                                            {type === "SHARED" && (
                                                <CommentEditor
                                                    id={`subject-comment-${subject.templateId}`}
                                                    label={`Add comment for ${subject.subjectName}`}
                                                    placeholder={`Add comment for ${subject.subjectName}`}
                                                    isVisible={!!subjectCommentsVisible[subject.templateId]}
                                                    onToggle={() => {
                                                        toggleSubjectCommentVisibility(subject.templateId);

                                                        const criteria = getCriteriaForSubject(subject);
                                                        seedSubjectCommentDraftIfMissing(
                                                            subject.templateId,
                                                            criteria?.comment
                                                        );
                                                    }}
                                                    canEdit={
                                                        subjectPerm.toUpperCase() === "EDIT" &&
                                                        isSharedWholeClass &&
                                                        isSharedActiveAndIsUnreleased
                                                    }
                                                    isSaving={savingTemplateId === subject.templateId}
                                                    value={getDraftOrSavedComment(subject)}
                                                    onChange={(next) =>
                                                        setSubjectCommentDrafts((prev) => ({
                                                            ...prev,
                                                            [subject.templateId]: next
                                                        }))
                                                    }
                                                    onSave={() => handleSaveSubjectComment(subject)}
                                                    viewValue={getDraftOrSavedComment(subject)}
                                                />
                                            )}
                                        </div>
                                    );
                                })}
                        </div>
                    </>
                ) : (
                    <div className="epr-students-table">
                        <span className="group-name-title">Students</span>
                        {loading ? (
                            <LoadingSvg2 />
                        ) : (
                            <table>
                                <thead>
                                    <tr>
                                        <th scope="col">Student</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>{studentTableRows}</tbody>
                            </table>
                        )}
                    </div>
                )}
            </Accordion.Body>
        </Accordion.Item>
    );
};

GroupAccordionItem.propTypes = {
    dispatch: PropTypes.func.isRequired,
    eventKey: PropTypes.string.isRequired,
    group: PropTypes.object.isRequired,
    groupRatingCounts: PropTypes.arrayOf(PropTypes.object),
    grade: PropTypes.object,
    handleDeleteGroup: PropTypes.func,
    isActiveAndIsUnreleased: PropTypes.bool,
    onEdit: PropTypes.func,
    selectedEpr: PropTypes.object,
    basePath: PropTypes.string,
    type: PropTypes.string
};

GroupAccordionItem.defaultProps = {
    handleDeleteGroup: null,
    isActiveAndIsUnreleased: false,
    onEdit: null,
    selectedEpr: null,
    type: "HOMEROOM"
};

export default GroupAccordionItem;
